import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Switch, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MediaLibrary from "../../components/ui/MediaLibrary";
import { getHelpVideos, HelpVideo, saveHelpVideo, uploadVideo } from "../../service/MediaService";
import { showSuccessToast } from "../../utils/Toast";

/**
 * Hjælpevideoerne i kundeportalen.
 *
 * Før stod adresserne hårdkodet i koden: en ny video krævede en kodeændring og en udrulning, og knappen på
 * forsiden kunne kun fjernes ved at kommentere den ud. Nu styres hver plads herfra - slås den fra, forsvinder
 * knappen fra kundens side.
 */
export default function HelpVideos() {
  const [t] = useTranslation("global");
  const [videos, setVideos] = useState<HelpVideo[]>([]);
  const [picking, setPicking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => getHelpVideos().then(setVideos);
  useEffect(() => {
    load();
  }, []);

  const change = async (slot: string, patch: Partial<HelpVideo>) => {
    setVideos((current) => current.map((video) => (video.slot === slot ? { ...video, ...patch } : video)));
    const saved = await saveHelpVideo(slot, patch);
    if (saved) showSuccessToast(t("media.saved"), false);
    load();
  };

  const upload = async (slot: string, file?: File) => {
    if (!file) return;
    setBusy(true);
    const asset = await uploadVideo(file);
    setBusy(false);
    if (asset) {
      await change(slot, { mediaKey: asset.key });
      showSuccessToast(t("media.videoProcessing"), false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("media.helpIntro")}
      </Typography>

      <Dialog open={picking !== null} onClose={() => setPicking(null)} fullWidth maxWidth="md">
        <DialogTitle>{t("media.chooseVideo")}</DialogTitle>
        <DialogContent>
          <MediaLibrary
            onPick={(asset) => {
              if (picking) change(picking, { mediaKey: asset.key });
              setPicking(null);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPicking(null)}>{t("media.cancel")}</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 2 }}>
        {videos.map((video) => (
          <Paper key={video.slot} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                {t(`media.slots.${video.slot}`)}
              </Typography>
              <FormControlLabel
                control={<Switch checked={Boolean(video.active)} onChange={(event) => change(video.slot, { active: event.target.checked })} />}
                label={t("media.active")}
              />
            </Box>

            {video.mediaUrl ? (
              <Box component="video" src={video.mediaUrl} controls preload="metadata" sx={{ width: "100%", borderRadius: 1, bgcolor: "#000", maxHeight: 200 }} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {video.youtubeUrl || t("media.noVideo")}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setPicking(video.slot)}>
                {t("media.chooseVideo")}
              </Button>
              <Button size="small" variant="outlined" component="label" disabled={busy}>
                {t("media.uploadVideo")}
                <input hidden type="file" accept="video/*" onChange={(event) => upload(video.slot, event.target.files?.[0])} />
              </Button>
            </Box>

            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label={t("media.youtube")}
              value={video.youtubeUrl || ""}
              onChange={(event) => setVideos((current) => current.map((one) => (one.slot === video.slot ? { ...one, youtubeUrl: event.target.value } : one)))}
              onBlur={(event) => event.target.value !== "" && change(video.slot, { youtubeUrl: event.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              label={t("media.videoTitle")}
              value={video.title || ""}
              onChange={(event) => setVideos((current) => current.map((one) => (one.slot === video.slot ? { ...one, title: event.target.value } : one)))}
              onBlur={(event) => change(video.slot, { title: event.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              label={t("media.buttonLabel")}
              value={video.label || ""}
              onChange={(event) => setVideos((current) => current.map((one) => (one.slot === video.slot ? { ...one, label: event.target.value } : one)))}
              onBlur={(event) => change(video.slot, { label: event.target.value })}
            />
          </Paper>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        {t("media.helpHint")}
      </Typography>
    </Box>
  );
}
